// DocumentsTab.js — Mortgage Matrix Broker CRM
// v2.2.1 — ✅ Silent initial fetch + no “Failed to fetch” on load
// ---------------------------------------------------------------------------------
// ✅ Auto-loads existing link/uploads in the background (no scary error)
// ✅ Broker flow: select categories → create upload link → send SMS / copy link
// ✅ Errors shown only on user actions (Refresh/Create/Send/View/Download)
// ✅ Uses your HTTP API Gateway base: https://ucxra0w6z7.execute-api.ap-southeast-2.amazonaws.com

import React, { useCallback, useEffect, useMemo, useState } from "react"
import styles from "../BrokerCRM.module.css"

// -----------------------------
// CONFIG — edit these only
// -----------------------------
const BASE_URL = "https://ucxra0w6z7.execute-api.ap-southeast-2.amazonaws.com" // your API Gateway invoke URL

const API = {
  register: (oppId) => `/documents/register?opportunityId=${encodeURIComponent(oppId)}`,
  requestCategories: `/documents/request-categories`,
  // If you already have a separate SMS service, keep it. Otherwise wire later.
  sendSms: `/sms/send-docs-link`,
  viewUrl: (oppId, docKey) =>
    `/documents/view-url?opportunityId=${encodeURIComponent(oppId)}&docKey=${encodeURIComponent(docKey)}`,
  downloadUrl: (oppId, docKey) =>
    `/documents/download-url?opportunityId=${encodeURIComponent(oppId)}&docKey=${encodeURIComponent(docKey)}`,
}

// -----------------------------
// Categories
// -----------------------------
const CATS = [
  { id: "id", title: "ID", subtitle: "Driver’s Licence or Passport", hint: "Identification for any applicant.", icon: "🪪" },
  { id: "payg", title: "PAYG Income", subtitle: "2 most recent payslips", hint: "If applicants are employed (PAYG).", icon: "💼" },
  { id: "self_employed", title: "Self-employed", subtitle: "Tax return + Notice of Assessment", hint: "ABN / company / trust income.", icon: "📊" },
  { id: "other_income", title: "Other income", subtitle: "Rental / dividends / Centrelink", hint: "Extra income sources needing evidence.", icon: "💰" },
]

// -----------------------------
// Helpers
// -----------------------------
async function apiFetch(path, options = {}) {
  const res = await fetch(`${BASE_URL}${path}`, {
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
    ...options,
  })

  const text = await res.text()
  let json = null
  try {
    json = text ? JSON.parse(text) : null
  } catch {
    json = null
  }

  if (!res.ok) {
    const msg =
      json?.message ||
      json?.error ||
      (text ? String(text).slice(0, 220) : "") ||
      `Request failed (${res.status})`
    const err = new Error(msg)
    err.status = res.status
    throw err
  }

  return json
}

const digitsOnly = (v) => String(v ?? "").replace(/[^\d]/g, "")

const formatSize = (bytes) => {
  const b = Number(bytes || 0)
  if (!b || !Number.isFinite(b)) return ""
  const mb = b / 1024 / 1024
  return `${mb.toFixed(2)} MB`
}

const fmtDateTime = (iso) => {
  if (!iso) return "—"
  try {
    const d = new Date(iso)
    return d.toLocaleString("en-AU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  } catch {
    return iso
  }
}

const inferSuggestedCatsFromIncome = (incomeRows = []) => {
  const rows = Array.isArray(incomeRows) ? incomeRows : []
  const blob = rows
    .map((r) => `${r?.incomeType || ""} ${r?.employerName || ""} ${r?.roleDescription || ""} ${r?.category || ""}`)
    .join(" ")
    .toLowerCase()

  const hintsSelf = ["abn", "pty", "company", "director", "trust", "sole", "self", "contractor", "subcontract"]
  const hasEmployment = rows.some((r) => String(r?.incomeType || "") === "Employment")
  const isSelf = hintsSelf.some((h) => blob.includes(h))

  const suggested = new Set()
  suggested.add("id")
  if (isSelf) suggested.add("self_employed")
  if (hasEmployment) suggested.add("payg")
  return Array.from(suggested)
}

const statusPill = (status) => {
  const s = String(status || "").toLowerCase()
  if (s === "verified") return { label: "Verified", className: `${styles.pillBase} ${styles.pillVerified}` }
  if (s === "uploaded") return { label: "Uploaded", className: `${styles.pillBase} ${styles.pillUploaded}` }
  if (s === "requested") return { label: "Pending", className: `${styles.pillBase} ${styles.pillRequested}` }
  return { label: "Pending", className: `${styles.pillBase} ${styles.pillNotRequested}` }
}

// -----------------------------
// Component
// -----------------------------
export default function DocumentsTab({ mercuryOpportunityId, clientMobile, incomeRows }) {
  const oppId = String(mercuryOpportunityId || "").trim()
  const canOperate = !!oppId

  const [loading, setLoading] = useState(false)
  const [topBusy, setTopBusy] = useState(false)
  const [rowBusy, setRowBusy] = useState("")
  const [error, setError] = useState("")
  const [notice, setNotice] = useState("")
  const [uploadLink, setUploadLink] = useState("")
  const [uploads, setUploads] = useState([])

  // if true: suppress initial “Failed to fetch” noise
  const [didInitialLoadAttempt, setDidInitialLoadAttempt] = useState(false)

  const suggestedCats = useMemo(() => inferSuggestedCatsFromIncome(incomeRows), [incomeRows])
  const [selectedCats, setSelectedCats] = useState([])

  const [suggestedApplied, setSuggestedApplied] = useState(false)
  useEffect(() => {
    if (suggestedApplied) return
    if (Array.isArray(suggestedCats) && suggestedCats.length) {
      setSelectedCats(suggestedCats)
      setSuggestedApplied(true)
    }
  }, [suggestedCats, suggestedApplied])

  const receivedCount = useMemo(
    () => uploads.filter((u) => ["uploaded", "verified"].includes(String(u?.status || "").toLowerCase())).length,
    [uploads]
  )
  const verifiedCount = useMemo(
    () => uploads.filter((u) => String(u?.status || "").toLowerCase() === "verified").length,
    [uploads]
  )

  const refresh = useCallback(
    async ({ silent = false } = {}) => {
      if (!canOperate) {
        setLoading(false)
        setError("")
        setNotice("")
        setUploadLink("")
        setUploads([])
        return
      }

      setLoading(true)
      if (!silent) {
        setError("")
        setNotice("")
      }

      try {
        const res = await apiFetch(API.register(oppId), { method: "GET" })
        setUploadLink(res?.uploadLink || "")
        setUploads(Array.isArray(res?.uploads) ? res.uploads : Array.isArray(res?.items) ? res.items : [])
      } catch (e) {
        // ONLY show error if not silent
        if (!silent) {
          const msg =
            e?.status === 404 ? "Documents service is not available yet." : e?.message || "Could not load documents."
          setError(msg)
        }
      } finally {
        setLoading(false)
      }
    },
    [canOperate, oppId]
  )

  // Silent initial load (don’t scare the broker)
  useEffect(() => {
    if (!canOperate) return
    if (didInitialLoadAttempt) return
    setDidInitialLoadAttempt(true)
    refresh({ silent: true })
  }, [canOperate, didInitialLoadAttempt, refresh])

  const toggleCat = (id) => {
    setError("")
    setNotice("")
    setSelectedCats((prev) => {
      const s = new Set(prev || [])
      if (s.has(id)) s.delete(id)
      else s.add(id)
      return Array.from(s)
    })
  }

  const copyLink = async () => {
    if (!uploadLink) return
    try {
      await navigator.clipboard.writeText(uploadLink)
      setNotice("Link copied.")
      setTimeout(() => setNotice(""), 1200)
    } catch {
      setNotice("Could not copy link on this browser.")
      setTimeout(() => setNotice(""), 1600)
    }
  }

  const sendSms = async () => {
    const mob = digitsOnly(clientMobile)
    if (!mob) return setError("Client mobile is missing.")
    if (!uploadLink) return setError("Create the upload link first.")

    setTopBusy(true)
    setError("")
    setNotice("")
    try {
      await apiFetch(API.sendSms, {
        method: "POST",
        body: JSON.stringify({ mobile: mob, uploadLink, variant: "documents_upload" }),
      })
      setNotice("SMS sent.")
      setTimeout(() => setNotice(""), 1200)
    } catch (e) {
      setError(e?.message || "Failed to send SMS.")
    } finally {
      setTopBusy(false)
    }
  }

  const createUploadLink = async () => {
    if (!canOperate) return
    if (!selectedCats.length) return setError("Select at least one category.")

    setTopBusy(true)
    setError("")
    setNotice("")
    try {
      const res = await apiFetch(API.requestCategories, {
        method: "POST",
        body: JSON.stringify({ opportunityId: oppId, categories: selectedCats }),
      })

      if (res?.uploadLink) setUploadLink(res.uploadLink)
      setNotice("Upload link created.")
      setTimeout(() => setNotice(""), 1200)

      // post-create: do a normal refresh (and show errors if any)
      await refresh({ silent: false })
    } catch (e) {
      setError(e?.message || "Could not create upload link.")
    } finally {
      setTopBusy(false)
    }
  }

  const viewDoc = async (docKey) => {
    if (!canOperate || !docKey) return
    setRowBusy(docKey)
    setError("")
    try {
      const { url } = await apiFetch(API.viewUrl(oppId, docKey), { method: "GET" })
      if (url) window.open(url, "_blank", "noopener,noreferrer")
    } catch (e) {
      setError(e?.message || "Could not open file.")
    } finally {
      setRowBusy("")
    }
  }

  const downloadDoc = async (docKey) => {
    if (!canOperate || !docKey) return
    setRowBusy(docKey)
    setError("")
    try {
      const { url } = await apiFetch(API.downloadUrl(oppId, docKey), { method: "GET" })
      if (url) window.open(url, "_blank", "noopener,noreferrer")
    } catch (e) {
      setError(e?.message || "Could not download file.")
    } finally {
      setRowBusy("")
    }
  }

  // -----------------------------
  // Cool minimal inline styles
  // -----------------------------
  const topTitle = { fontSize: 26, fontWeight: 650, color: "#141414", letterSpacing: 0.1 }
  const topSub = { marginTop: 6, fontSize: 13, fontWeight: 450, color: "rgba(20,20,20,0.62)" }
  const sectionLabel = { marginTop: 18, fontWeight: 600, marginBottom: 10, fontSize: 14, color: "rgba(20,20,20,0.92)" }

  const actionRow = { marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" }

  const primaryCool = (disabled) => ({
    height: 44,
    padding: "0 16px",
    borderRadius: 12,
    border: "1px solid rgba(20,20,20,0.85)",
    background: "#141414",
    color: "#fff",
    fontWeight: 600,
    fontSize: 14,
    letterSpacing: 0.1,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.55 : 1,
    transition: "transform 120ms ease, box-shadow 180ms ease, opacity 180ms ease",
  })

  const outlineCool = (disabled) => ({
    height: 44,
    padding: "0 16px",
    borderRadius: 12,
    border: "1px solid rgba(20,20,20,0.14)",
    background: "#fff",
    color: "rgba(20,20,20,0.86)",
    fontWeight: 600,
    fontSize: 14,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.55 : 1,
  })

  const accentCool = (disabled) => ({
    height: 44,
    padding: "0 16px",
    borderRadius: 12,
    border: "1px solid rgba(78,220,246,0.45)",
    background: "rgba(78,220,246,0.12)",
    color: "rgba(20,20,20,0.92)",
    fontWeight: 600,
    fontSize: 14,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.55 : 1,
  })

  const card = (active = false) => ({
    borderRadius: 16,
    padding: 14,
    border: active ? "1px solid rgba(78,220,246,0.55)" : "1px solid rgba(20,20,20,0.12)",
    background: active ? "rgba(78,220,246,0.08)" : "#fff",
    cursor: "pointer",
    boxShadow: active ? "0 10px 22px rgba(78, 220, 246, 0.14)" : "none",
    transition: "0.15s",
    display: "flex",
    gap: 12,
    alignItems: "flex-start",
  })

  const check = (active) => ({
    width: 18,
    height: 18,
    borderRadius: 999,
    border: active ? "1px solid rgba(20,20,20,0.70)" : "1px solid rgba(20,20,20,0.22)",
    background: active ? "rgba(78,220,246,0.95)" : "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: 12,
    lineHeight: 1,
  })

  return (
    <div className={styles.outerCard}>
      <div className={styles.innerCard}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            <div style={topTitle}>Documents</div>
            <div style={topSub}>Select what you need, then send the secure upload link.</div>
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => refresh({ silent: false })}
              disabled={!canOperate || loading || topBusy}
              className={styles.secondaryButton}
            >
              {loading ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        </div>

        {/* Gate */}
        {!canOperate && (
          <div className={styles.docsNotice}>
            <div className={styles.docsNoticeTitle}>Available after Review &amp; Submit</div>
            <div className={styles.docsNoticeText}>Once Mercury IDs exist, you can create the upload link and send it by SMS.</div>
          </div>
        )}

        {/* Messages */}
        {error ? <div className={styles.docsError}>{error}</div> : null}
        {notice ? (
          <div className={styles.docsNotice} style={{ background: "rgba(34,197,94,0.10)", borderColor: "rgba(34,197,94,0.22)" }}>
            <div className={styles.docsNoticeTitle} style={{ fontWeight: 600 }}>
              {notice}
            </div>
          </div>
        ) : null}

        {/* Category selector */}
        <div style={{ marginTop: 10 }}>
          <div style={sectionLabel}>What do you need?</div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 }}>
            {CATS.map((c) => {
              const active = selectedCats.includes(c.id)
              return (
                <div key={c.id} onClick={() => toggleCat(c.id)} style={card(active)} role="button" tabIndex={0}>
                  <div style={{ fontSize: 20, lineHeight: 1, marginTop: 2, opacity: 0.95 }}>{c.icon}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                      <div style={{ fontWeight: 600, fontSize: 15, color: "#141414" }}>{c.title}</div>
                      <div style={check(active)}>{active ? "✓" : ""}</div>
                    </div>

                    <div style={{ marginTop: 6, fontWeight: 500, color: "rgba(20,20,20,0.80)" }}>{c.subtitle}</div>
                    <div style={{ marginTop: 6, fontWeight: 400, color: "rgba(20,20,20,0.58)", fontSize: 13 }}>{c.hint}</div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Actions */}
          <div style={actionRow}>
            <button
              type="button"
              onClick={createUploadLink}
              disabled={!canOperate || topBusy || !selectedCats.length}
              style={primaryCool(!canOperate || topBusy || !selectedCats.length)}
            >
              {topBusy ? "Working…" : "Create upload link"}
            </button>

            <button
              type="button"
              onClick={sendSms}
              disabled={!canOperate || topBusy || !uploadLink || !digitsOnly(clientMobile)}
              style={accentCool(!canOperate || topBusy || !uploadLink || !digitsOnly(clientMobile))}
              title={!uploadLink ? "Create upload link first" : ""}
            >
              {topBusy ? "Working…" : "Send link (SMS)"}
            </button>

            <button type="button" onClick={copyLink} disabled={!uploadLink} style={outlineCool(!uploadLink)}>
              Copy link
            </button>
          </div>
        </div>

        {/* Upload link */}
        <div className={styles.docsUploadLinkBox} style={{ marginTop: 14 }}>
          <div className={styles.docsUploadLabel}>Secure upload link</div>
          <div className={styles.docsUploadValue}>
            {uploadLink ? uploadLink : "Not created yet. Select a category and click “Create upload link”."}
          </div>
        </div>

        {/* Uploaded docs */}
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "rgba(20,20,20,0.92)" }}>
            Uploaded documents{" "}
            <span style={{ fontWeight: 450, color: "rgba(20,20,20,0.60)" }}>
              ({receivedCount} received • {verifiedCount} verified)
            </span>
          </div>

          {loading ? (
            <div style={{ marginTop: 10, fontWeight: 450, color: "rgba(20,20,20,0.70)" }}>Loading…</div>
          ) : uploads.length === 0 ? (
            <div
              style={{
                marginTop: 10,
                padding: 14,
                borderRadius: 16,
                border: "1px solid rgba(20,20,20,0.10)",
                background: "#fff",
                fontWeight: 450,
                color: "rgba(20,20,20,0.65)",
              }}
            >
              No uploads yet.
            </div>
          ) : (
            <div style={{ marginTop: 10, display: "grid", gap: 10 }}>
              {uploads.map((u) => {
                const docKey = u.docKey || u.key || ""
                const busy = rowBusy === docKey
                const pill = statusPill(u.status)

                return (
                  <div
                    key={docKey || `${u.fileName}-${u.uploadedAt}`}
                    style={{
                      padding: 14,
                      borderRadius: 16,
                      border: "1px solid rgba(20,20,20,0.10)",
                      background: "#fff",
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 14,
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ minWidth: 260 }}>
                      <div style={{ fontWeight: 600, color: "#141414" }}>
                        {u.docLabel || u.document || u.docType || "Document"}
                      </div>
                      <div style={{ marginTop: 6, fontWeight: 450, color: "rgba(20,20,20,0.65)" }}>
                        {u.applicantLabel ? `${u.applicantLabel} • ` : ""}
                        {u.fileName || "File"} {u.fileSize ? `• ${formatSize(u.fileSize)}` : ""}
                      </div>
                      <div style={{ marginTop: 6, fontWeight: 400, color: "rgba(20,20,20,0.55)", fontSize: 13 }}>
                        Uploaded: {fmtDateTime(u.uploadedAt)} {u.verifiedAt ? ` • Verified: ${fmtDateTime(u.verifiedAt)}` : ""}
                      </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <span className={pill.className}>{pill.label}</span>

                      <button type="button" onClick={() => viewDoc(docKey)} disabled={!docKey || busy} className={styles.secondaryButton}>
                        {busy ? "…" : "View"}
                      </button>

                      <button
                        type="button"
                        onClick={() => downloadDoc(docKey)}
                        disabled={!docKey || busy}
                        className={styles.secondaryButton}
                      >
                        {busy ? "…" : "Download"}
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div className={styles.docsTip}>Tip: Select categories → Create upload link → Send link (SMS).</div>
      </div>
    </div>
  )
}